import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useEffect, useEffectEvent, useLayoutEffect } from 'react';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { ConnectionState } from 'utils/connectionState';
import {
  getNegotiatedConnection,
  receiveNotifications,
  stopNotifications,
} from '../Notifications/signalrConnector';
import { setGeneratedFormDocuments } from 'features/form/formReducer';
import { updateConnection } from 'store/notificationsReducer';
import { PdfProcessedEvent } from 'types/pushNotifications';
import { CampaignUpdatedEvent } from 'Api/Campaigns/Types/types';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import { emitPdfProcessedEvent } from 'hooks/usePdfProcessedEvent';
import { AuthContext, getBrowserSessionId } from '@urbanx/agx.auth.client';
import { HUB_NAMES } from 'constants/signalrConstants';

interface SetupNotificationsLayoutProps {
  children: React.ReactNode;
}

export const SetupNotificationsLayout = ({ children }: SetupNotificationsLayoutProps) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { userAccount, getAuthToken, isLoggingOut } = useContext(AuthContext);

  const { connectionState, connectionUpdatedAt } = useAppSelector((state) => state.notifications);

  const markCampaignAsStale = useCampaignStaleMarker();
  const markCampaignAsStaleEvent = useEffectEvent((campaignId: string) =>
    markCampaignAsStale(campaignId)
  );

  // Add a lock to prevent the browser tab from sleeping while it isn't opened
  useEffect(() => {
    if (navigator.locks?.request) {
      let releaseLock: (() => void) | undefined;
      const lock = new Promise<void>((resolve) => {
        releaseLock = resolve;
      });

      void navigator.locks.request('autopilot_shared_lock', { mode: 'shared' }, () => lock);

      return () => releaseLock?.();
    }
  }, []);

  useEffect(() => {
    if (connectionState === ConnectionState.Disconnected) {
      console.error('AutoPilot disconnected.');
    }
  }, [connectionState, connectionUpdatedAt]);

  const { user, userType, organisationId, agency } = userAccount;
  const scopeKey =
    user && !isLoggingOut ? JSON.stringify([user, userType, organisationId, agency]) : undefined;

  // Token-function refreshes keep the same authority and must preserve valid fetches.
  useLayoutEffect(() => {
    if (!scopeKey) return;
    return () => {
      void queryClient.cancelQueries();
    };
  }, [scopeKey, queryClient]);

  useEffect(() => {
    if (!scopeKey) return;

    let disposed = false;

    const hubCon = getNegotiatedConnection(HUB_NAMES.APP_NOTIFICATIONS, getAuthToken);
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    hubCon.onclose(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Disconnected));
    });

    hubCon.onreconnecting(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Reconnecting));
    });

    hubCon.onreconnected(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Connected));
    });

    const onPdf = async (data: PdfProcessedEvent) => {
      if (disposed) return;
      if (data.sessionId !== getBrowserSessionId()) return;
      dispatch(setGeneratedFormDocuments(data));
      await emitPdfProcessedEvent(data);
    };

    const onCampaign = async (data: CampaignUpdatedEvent) => {
      if (disposed) return;
      await markCampaignAsStaleEvent(data.campaignId);
    };
    receiveNotifications(hubCon, 'PdfProcessedEvent', onPdf);
    receiveNotifications(hubCon, 'CampaignUpdatedEvent', onCampaign);

    const startConnection = async () => {
      dispatch(updateConnection(ConnectionState.Connecting));

      try {
        await hubCon.start();
        if (disposed) {
          await hubCon.stop();
          return;
        }
        dispatch(updateConnection(ConnectionState.Connected));
      } catch (error) {
        if (!disposed) {
          console.error('Error starting AutoPilot notification connection', error);
          dispatch(updateConnection(ConnectionState.Disconnected));
          retryTimeout = setTimeout(startConnection, 5000);
        }
      }
    };

    void startConnection();

    return () => {
      disposed = true;
      clearTimeout(retryTimeout);
      stopNotifications(hubCon, 'PdfProcessedEvent', onPdf);
      stopNotifications(hubCon, 'CampaignUpdatedEvent', onCampaign);
      void hubCon.stop().catch((error) => {
        console.error('Error stopping AutoPilot notification connection', error);
      });
    };
  }, [dispatch, getAuthToken, scopeKey]);

  return children;
};
